// Display time zone handling. The app shows datetimes in the user's profile time zone
// (`me.account.timezone`), falling back to the browser zone. Date-only values are not affected
// (they are calendar dates stored as UTC midnight).

export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

let displayZone = browserTimeZone();
const listeners = new Set<() => void>();

export function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

export function setDisplayTimeZone(tz: string | null | undefined): void {
  const next = tz && isValidTimeZone(tz) ? tz : browserTimeZone();
  if (next === displayZone) return;
  displayZone = next;
  listeners.forEach((l) => l());
}

export function getDisplayTimeZone(): string {
  return displayZone;
}

export function onTimeZoneChange(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** All IANA zones known to the browser (fallback: a short list). */
export function allTimeZones(): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (k: string) => string[] };
  try {
    const list = intl.supportedValuesOf?.("timeZone");
    if (list && list.length) return list.includes("UTC") ? list : ["UTC", ...list];
  } catch {
    /* ignore */
  }
  return ["UTC", "Europe/Zurich", "Europe/Berlin", "Europe/London", "America/New_York", "America/Los_Angeles", "Asia/Tokyo"];
}

const partsFmtCache = new Map<string, Intl.DateTimeFormat>();
function partsFmt(tz: string): Intl.DateTimeFormat {
  let f = partsFmtCache.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    partsFmtCache.set(tz, f);
  }
  return f;
}

export interface ZonedParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number;
  minute: number;
  second: number;
}

/** Wall-clock parts of an instant in a time zone. */
export function zonedParts(d: Date, tz: string = displayZone): ZonedParts {
  const out: Record<string, number> = {};
  for (const p of partsFmt(tz).formatToParts(d)) {
    if (p.type !== "literal") out[p.type] = Number(p.value);
  }
  return {
    year: out.year,
    month: out.month,
    day: out.day,
    hour: out.hour === 24 ? 0 : out.hour,
    minute: out.minute,
    second: out.second,
  };
}

const pad = (n: number) => String(n).padStart(2, "0");

export function ymdOf(p: Pick<ZonedParts, "year" | "month" | "day">): string {
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}

/** YYYY-MM-DD of an instant in the zone. */
export function zonedYmd(d: Date, tz: string = displayZone): string {
  return ymdOf(zonedParts(d, tz));
}

/** HH:MM of an instant in the zone. */
export function zonedHm(d: Date, tz: string = displayZone): string {
  const p = zonedParts(d, tz);
  return `${pad(p.hour)}:${pad(p.minute)}`;
}

/** Offset (ms) of the zone at the given instant: wall-clock minus UTC. */
function offsetAt(ts: number, tz: string): number {
  const p = zonedParts(new Date(ts), tz);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - Math.floor(ts / 1000) * 1000;
}

/** Instant for a wall-clock date + time ("HH:MM") in the zone. */
export function zonedToDate(ymd: string, hm: string, tz: string = displayZone): Date {
  const [y, m, d] = ymd.split("-").map(Number);
  const [hh, mm] = hm.split(":").map(Number);
  const guess = Date.UTC(y, m - 1, d, hh, mm);
  let ts = guess - offsetAt(guess, tz);
  // Second pass handles DST transitions.
  const o2 = offsetAt(ts, tz);
  ts = guess - o2;
  return new Date(ts);
}

/** Today's calendar date in the zone. */
export function todayYmd(now: Date = new Date(), tz: string = displayZone): string {
  return zonedYmd(now, tz);
}

/** Calendar arithmetic on YYYY-MM-DD strings (zone-free). */
export function addDaysYmd(ymd: string, days: number): string {
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
}

/** Whole days from a to b (b - a). */
export function diffDaysYmd(a: string, b: string): number {
  const [y1, m1, d1] = a.split("-").map(Number);
  const [y2, m2, d2] = b.split("-").map(Number);
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86400000);
}

/** 0 = Monday … 6 = Sunday. */
export function weekdayMon0(ymd: string): number {
  const [y, m, d] = ymd.split("-").map(Number);
  return (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7;
}

/** ISO-8601 week number. */
export function isoWeek(ymd: string): number {
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const day = (dt.getUTCDay() + 6) % 7;
  dt.setUTCDate(dt.getUTCDate() - day + 3); // Thursday of this week decides the year
  const jan1 = Date.UTC(dt.getUTCFullYear(), 0, 1);
  return 1 + Math.floor((dt.getTime() - jan1) / 86400000 / 7);
}
